/**
 * Derived and formatted fields the patient lists show (EN-5169 / EN-5171).
 */

/**
 * How long a patient was monitored — first login to archive — ported from the
 * `getArchivedPatients` transformer on Retool's `page_ArchivedPatients`
 * (EN-5171).
 *
 * Derived in the portal rather than added to the API for the same reason Retool
 * derived it: it is presentation over two dates the row already carries, and the
 * endpoint serves Retool too while both run.
 */

/** `"12 days, 04:30"`, or null when it cannot be computed. */
export function formatMonitoringLength(
  archivedDate: string | null,
  firstLoginDate: string | null
): string | null {
  if (!archivedDate || !firstLoginDate) return null;

  const archived = new Date(archivedDate).getTime();
  const firstLogin = new Date(firstLoginDate).getTime();

  if (Number.isNaN(archived) || Number.isNaN(firstLogin)) return null;

  const diffMs = archived - firstLogin;
  // Retool guarded this too ("evita datas trocadas"): a patient archived before
  // their first login means the dates disagree, and a negative duration says
  // nothing useful. Reachable in real data — the archive event is backfilled
  // from cms_patientarchiveevent and first_login is set by the app.
  if (diffMs < 0) return null;

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  const pad = (value: number) => (value < 10 ? `0${value}` : String(value));

  return `${days} days, ${pad(hours)}:${pad(minutes)}`;
}

/**
 * The badge tone for the API's three-state `passwordSet` string.
 *
 * ⚠️ The values are `"Set"` / `"Not set"` / `"No data added"`, not a yes/no —
 * both the patient list and the user endpoint build them from the same CASE over
 * `cms_user.password` and `last_used`. "No data added" means a password exists
 * but the patient has never opened the app, which is neither of the other two.
 */
export function passwordSetTone(
  passwordSet: string | null | undefined
): "active" | "inactive" | "warning" {
  if (passwordSet === "Set") return "active";
  if (passwordSet === "No data added") return "warning";
  return "inactive";
}
